import { useEffect, useRef } from "react";

import { registerSuperProperties } from "@/shared/lib/analytics";
import { useSectorAccessState } from "@/core/hooks/useSectorAccessState";

/**
 * Registra `onboarding_mode` (snapshot efetivo do modo de onboarding da Company,
 * lido do access-state do sector ativo) como super property do PostHog. Assim os
 * eventos client-side também carregam o modo REAL — a mesma dimensão que os
 * eventos server-side ganham em `captureCompanyEvent` (BFC P9) — permitindo
 * cortar funis client (checkout, onboarding pós-company) por modo.
 *
 * Diferente de `onboarding_variant` (bucket da feature flag `onboarding-with-card`):
 * `onboarding_mode` é o controle real (toggle `plans-settings` → snapshot na
 * subscription). Ver docs/analytics/README.md ("onboarding_variant × onboarding_mode").
 *
 * Cobertura: o `sectorId` é mascarado durante o onboarding-v2, então o modo só é
 * registrado quando o access-state do sector ativo é conhecido (tipicamente
 * pós-onboarding). Os eventos server-side cobrem o período inteiro.
 *
 * Reseta o ref quando o modo fica desconhecido (ex.: logout, que chama
 * `posthog.reset()` e limpa as super properties) para re-registrar ao voltar.
 */
export function useRegisterOnboardingMode(sectorId?: string | null): void {
  const { data } = useSectorAccessState(sectorId);
  const mode = data.onboarding_mode;
  const lastRegistered = useRef<string | null>(null);

  useEffect(() => {
    if (!mode) {
      lastRegistered.current = null;
      return;
    }
    if (lastRegistered.current === mode) return;
    lastRegistered.current = mode;
    registerSuperProperties({ onboarding_mode: mode });
  }, [mode]);
}
